import { createRng } from "../util/rng.js";

/**
 * Generative ambient bed. No assets, no loops, no licensing exposure.
 *
 * Every procedural value comes from a seeded RNG (AC.17): the same seed and the
 * same wall-clock start render identical PCM and an identical event schedule.
 * `Math.random()` is lint-banned in this directory, which is why this module
 * only ever calls `rng.*`.
 *
 * The arrangement follows the wall clock, like the narration. Time is divided
 * into sections (`SECTION_SEC`) grouped into movements. A movement fixes the
 * key; a section fixes the chord, which layers are playing, and the effect
 * settings. Both are seeded by their index alone, so every listener hears the
 * same section at the same moment, whenever their bed was built. The bed's own
 * seed only varies micro-detail (detune, LFO rates).
 *
 * Changes are glides, never steps: layers fade in and out, chords and the key
 * slide, and the reverb, echo and tone filter are automated per section and
 * modulated by slow LFOs on top, so the space itself keeps moving.
 *
 * The bed is built for a long horizon with every event scheduled up front.
 * That avoids rebuilding at every 60 s boundary, which would click.
 */

export type MusicLayer = "pad" | "pluck" | "shimmer" | "bells" | "noise";

export interface MusicEvent {
  at: number;
  kind: "bell" | "drone" | "noise" | "pad" | "pluck" | "shimmer";
  frequency: number;
  durationSec: number;
  gain: number;
}

export interface MusicBedOptions {
  seed: number | string;
  /** Context time the bed starts. */
  startTime?: number;
  /** How far ahead events are scheduled. */
  horizonSec?: number;
  /** Overrides the movement's key root. */
  rootHz?: number;
  /** Fade-in length, so the bed never starts with a step. */
  fadeInSec?: number;
  /**
   * Wall-clock epoch milliseconds at `startTime`. The arrangement is placed
   * against this, so it must be the real clock in production. Defaults to 0,
   * which keeps offline renders reproducible.
   */
  wallClockMs?: number;
}

export interface MusicBed {
  output: GainNode;
  events: MusicEvent[];
  startAt: number;
  endsAt: number;
  stop(when: number, fadeSec?: number): void;
  dispose(): void;
}

// --- arrangement ------------------------------------------------------------

/** One section: a chord, a set of layers, and an effect setting. */
export const SECTION_SEC = 120;
/** Sections per movement; a movement holds one key. 16 minutes. */
export const SECTIONS_PER_MOVEMENT = 8;
/** Layer, chord and effect changes glide over roughly this long. */
export const SECTION_GLIDE_SEC = 20;
/** Key changes slide the drones more slowly still. */
export const KEY_GLIDE_SEC = 45;

/** G1. Keys sit in [G1, D2], so drone partials stay in the bass register. */
const KEY_BASE_HZ = 49;
const KEY_OFFSETS = [0, 2, 3, 5, 7] as const;
const MODES: readonly (readonly number[])[] = [
  [0, 2, 3, 5, 7, 8, 10], // aeolian
  [0, 2, 3, 5, 7, 9, 10], // dorian
  [0, 2, 4, 5, 7, 9, 10], // mixolydian
];
/** Chord roots as scale degrees; the tonic is listed twice so it recurs most. */
const CHORD_DEGREES = [0, 0, 2, 3, 4, 5, 6] as const;
export const MUSIC_LAYERS: readonly MusicLayer[] = ["pad", "pluck", "shimmer", "bells", "noise"];

export interface MovementPlan {
  index: number;
  rootHz: number;
  mode: readonly number[];
}

export interface SectionPlan {
  index: number;
  movement: MovementPlan;
  /** Chord tones in semitones above the key root, ascending. */
  chord: number[];
  /** 0 = silent, otherwise 0.55..1. Noise never fully leaves. */
  presence: Record<MusicLayer, number>;
  /** Tone filter cutoff, Hz. */
  brightness: number;
  /** Reverb send, 0..1. */
  reverb: number;
  /** 0 = small room, 1 = large hall. */
  room: number;
  /** Echo send, 0..1. */
  echo: number;
}

export function movementPlan(index: number): MovementPlan {
  const rng = createRng(`music:movement:${index}`);
  const offset = rng.pick(KEY_OFFSETS);
  return { index, rootHz: KEY_BASE_HZ * Math.pow(2, offset / 12), mode: rng.pick(MODES) };
}

export function sectionIndexAt(wallClockSec: number): number {
  return Math.floor(wallClockSec / SECTION_SEC);
}

export function sectionPlan(index: number): SectionPlan {
  const movementIndex = Math.floor(index / SECTIONS_PER_MOVEMENT);
  const position = index - movementIndex * SECTIONS_PER_MOVEMENT;
  const movement = movementPlan(movementIndex);
  const rng = createRng(`music:section:${index}`);

  // Each movement swells and thins: sparse at its edges, fullest mid-way.
  const arc = Math.sin((Math.PI * (position + 0.5)) / SECTIONS_PER_MOVEMENT);

  // A movement opens on its tonic, so the key change is heard as a key change.
  const degree = position === 0 ? 0 : rng.pick(CHORD_DEGREES);
  const steps = rng.chance(0.4) ? [0, 2, 4, 6] : [0, 2, 4];
  const chord = steps.map((step) => scaleSemitone(movement.mode, degree + step));

  const roll = (probability: number) => (rng.chance(probability) ? rng.range(0.55, 1) : 0);
  const presence: Record<MusicLayer, number> = {
    pad: roll(0.4 + 0.45 * arc),
    pluck: roll(0.2 + 0.5 * arc),
    shimmer: roll(0.15 + 0.45 * arc),
    bells: roll(0.45 + 0.35 * arc),
    noise: rng.range(0.3, 1),
  };
  // Never leave only the drones and the wind for two minutes.
  if (presence.pad + presence.pluck + presence.shimmer + presence.bells === 0) {
    presence[rng.pick(["pad", "bells"] as const)] = rng.range(0.55, 1);
  }

  const brightness = 700 + 2400 * (0.35 * arc + 0.65 * rng.next());
  const reverb = rng.range(0.35, 1);
  const room = rng.next();
  const echo = presence.pluck > 0 ? rng.range(0.45, 1) : rng.range(0, 0.35);

  return { index, movement, chord, presence, brightness, reverb, room, echo };
}

function scaleSemitone(mode: readonly number[], degree: number): number {
  const octave = Math.floor(degree / mode.length);
  return (mode[degree - octave * mode.length] as number) + 12 * octave;
}

function semitones(hz: number, steps: number): number {
  return hz * Math.pow(2, steps / 12);
}

// --- levels -----------------------------------------------------------------
// Calibrated by rendering: at the music slider's top the bed sits around
// -14 dBFS RMS, loud enough to hold its own beside the voice, with the master
// limiter still catching every peak.

const BED_GAIN = 1.4;
const DRONE_LEVEL = 0.11;
const PAD_LEVEL = 0.05;
const SHIMMER_LEVEL = 0.012;
const PLUCK_LEVEL = 0.07;
const BELL_LEVEL = 0.055;
const NOISE_LEVEL = 0.09;
const REVERB_SEND = 0.45;
const ECHO_SEND = 0.4;

// --- buffers ----------------------------------------------------------------

/**
 * Exponentially decaying noise. Deterministic for a given label.
 *
 * `normalize = false` is used deliberately (a normalized convolution would make
 * level unpredictable across browsers), so the peak scale is the only thing
 * keeping the wet sum in range. A longer tail carries more energy, so it gets a
 * lower peak.
 */
function makeImpulseResponse(
  context: BaseAudioContext,
  seconds: number,
  label: string,
  impulsePeak: number,
): AudioBuffer {
  const rate = context.sampleRate;
  const length = Math.max(1, Math.floor(rate * seconds));
  const buffer = context.createBuffer(2, length, rate);
  const rng = createRng(label);
  let rawPeak = 0;
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    let previous = 0;
    for (let i = 0; i < length; i += 1) {
      const white = rng.next() * 2 - 1;
      // One-pole lowpass gives a darker tail than raw white noise.
      previous = previous * 0.62 + white * 0.38;
      const value = previous * Math.pow(1 - i / length, 2.4);
      data[i] = value;
      rawPeak = Math.max(rawPeak, Math.abs(value));
    }
  }
  if (rawPeak > 0) {
    const scale = impulsePeak / rawPeak;
    for (let channel = 0; channel < 2; channel += 1) {
      const data = buffer.getChannelData(channel);
      for (let i = 0; i < length; i += 1) data[i] = (data[i] as number) * scale;
    }
  }
  return buffer;
}

interface Rooms {
  small: AudioBuffer;
  large: AudioBuffer;
}

/** The responses depend only on the sample rate, so build them once per context. */
const roomCache = new WeakMap<BaseAudioContext, Rooms>();

function roomsFor(context: BaseAudioContext): Rooms {
  const cached = roomCache.get(context);
  if (cached) return cached;
  const rooms = {
    small: makeImpulseResponse(context, 2.2, "impulse-response:small", 0.07),
    large: makeImpulseResponse(context, 4.5, "impulse-response:large", 0.05),
  };
  roomCache.set(context, rooms);
  return rooms;
}

function makeNoiseBuffer(context: BaseAudioContext, rng: () => number, seconds = 4): AudioBuffer {
  const rate = context.sampleRate;
  const length = Math.max(1, Math.floor(rate * seconds));
  const buffer = context.createBuffer(1, length, rate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < length; i += 1) {
    const white = rng() * 2 - 1;
    // Brown-ish noise: integrated white, leaky so it cannot drift away.
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.2;
  }
  // Crossfade the loop seam so the repeat is inaudible.
  const seam = Math.min(2048, Math.floor(length / 8));
  for (let i = 0; i < seam; i += 1) {
    const t = i / seam;
    const head = data[i] as number;
    const tail = data[length - seam + i] as number;
    data[i] = head * t + tail * (1 - t);
  }
  return buffer;
}

// --- the bed ----------------------------------------------------------------

export function buildMusicBed(context: BaseAudioContext, options: MusicBedOptions): MusicBed {
  const startAt = options.startTime ?? 0;
  const horizonSec = options.horizonSec ?? 900;
  const fadeInSec = options.fadeInSec ?? 4;
  const wallStartSec = (options.wallClockMs ?? 0) / 1000;
  const endsAt = startAt + horizonSec;
  const rng = createRng(options.seed);

  const sources: AudioScheduledSourceNode[] = [];
  const nodes: AudioNode[] = [];
  const events: MusicEvent[] = [];
  const track = <T extends AudioNode>(node: T): T => {
    nodes.push(node);
    return node;
  };
  const startSource = <T extends AudioScheduledSourceNode>(source: T, at = startAt): T => {
    source.start(at);
    sources.push(source);
    return source;
  };

  const toContext = (wallSec: number) => startAt + (wallSec - wallStartSec);
  const sections: Array<{ plan: SectionPlan; at: number }> = [];
  for (let index = sectionIndexAt(wallStartSec); index <= sectionIndexAt(wallStartSec + horizonSec); index += 1) {
    sections.push({ plan: sectionPlan(index), at: toContext(index * SECTION_SEC) });
  }
  const rootOf = (plan: SectionPlan) => options.rootHz ?? plan.movement.rootHz;

  /** Hold the current section's value from the start, then glide at each boundary. */
  const automate = (param: AudioParam, value: (plan: SectionPlan) => number, glideSec = SECTION_GLIDE_SEC) => {
    sections.forEach((section, index) => {
      if (index === 0) param.setValueAtTime(value(section.plan), startAt);
      else param.setTargetAtTime(value(section.plan), section.at, glideSec / 3);
    });
  };

  /** A slow sine LFO added onto `param`. */
  const lfo = (param: AudioParam, rateHz: number, depth: number) => {
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(rateHz, startAt);
    const amount = track(context.createGain());
    amount.gain.setValueAtTime(depth, startAt);
    oscillator.connect(amount);
    amount.connect(param);
    startSource(oscillator);
  };

  // --- master: tone filter and fade-in -------------------------------------
  const output = track(context.createGain());
  output.gain.setValueAtTime(0, startAt);
  output.gain.linearRampToValueAtTime(BED_GAIN, startAt + fadeInSec);

  const tone = track(context.createBiquadFilter());
  tone.type = "lowpass";
  tone.Q.value = 0.35;
  automate(tone.frequency, (plan) => plan.brightness);
  lfo(tone.frequency, rng.range(0.01, 0.03), rng.range(150, 300));
  // Gentle glue compression: bell and pluck attacks are what limit how loud the
  // bed can be, so taming them buys level without raising the peaks.
  const glue = track(context.createDynamicsCompressor());
  glue.threshold.value = -24;
  glue.knee.value = 12;
  glue.ratio.value = 3;
  glue.attack.value = 0.02;
  glue.release.value = 0.4;
  tone.connect(glue);
  glue.connect(output);

  const dry = track(context.createGain());
  dry.connect(tone);

  // --- reverb: two rooms, crossfaded so the size of the space moves --------
  const rooms = roomsFor(context);
  const reverbSend = track(context.createGain());
  automate(reverbSend.gain, (plan) => plan.reverb * REVERB_SEND);
  lfo(reverbSend.gain, rng.range(0.007, 0.015), REVERB_SEND * 0.2);

  const small = track(context.createConvolver());
  small.normalize = false;
  small.buffer = rooms.small;
  const large = track(context.createConvolver());
  large.normalize = false;
  large.buffer = rooms.large;
  const smallReturn = track(context.createGain());
  const largeReturn = track(context.createGain());
  // Equal-power crossfade between the rooms.
  automate(smallReturn.gain, (plan) => Math.cos((plan.room * Math.PI) / 2));
  automate(largeReturn.gain, (plan) => Math.sin((plan.room * Math.PI) / 2));
  // And a slow sway between them within a section: one LFO, opposite signs.
  const sway = context.createOscillator();
  sway.type = "sine";
  sway.frequency.setValueAtTime(rng.range(0.008, 0.02), startAt);
  const swayLarge = track(context.createGain());
  swayLarge.gain.setValueAtTime(0.2, startAt);
  const swaySmall = track(context.createGain());
  swaySmall.gain.setValueAtTime(-0.2, startAt);
  sway.connect(swayLarge);
  sway.connect(swaySmall);
  swayLarge.connect(largeReturn.gain);
  swaySmall.connect(smallReturn.gain);
  startSource(sway);

  reverbSend.connect(small);
  reverbSend.connect(large);
  small.connect(smallReturn);
  large.connect(largeReturn);
  smallReturn.connect(tone);
  largeReturn.connect(tone);

  // --- echo: a darkening feedback delay whose time and feedback drift ------
  const echoSend = track(context.createGain());
  automate(echoSend.gain, (plan) => plan.echo * ECHO_SEND);
  const delay = track(context.createDelay(2));
  delay.delayTime.setValueAtTime(rng.range(0.55, 0.8), startAt);
  // A few milliseconds of slow drift: a faint tape-like pitch sway in the repeats.
  lfo(delay.delayTime, rng.range(0.03, 0.07), 0.004);
  const echoTone = track(context.createBiquadFilter());
  echoTone.type = "lowpass";
  echoTone.frequency.value = 2200;
  const feedback = track(context.createGain());
  feedback.gain.setValueAtTime(0.38, startAt);
  lfo(feedback.gain, rng.range(0.01, 0.02), 0.11);
  echoSend.connect(delay);
  delay.connect(echoTone);
  echoTone.connect(feedback);
  feedback.connect(delay);
  echoTone.connect(dry);
  // Echoes are reverberant too, so they sit in the same space.
  echoTone.connect(reverbSend);

  /** Route a layer into the effects. */
  const route = (node: AudioNode, sends: { reverb?: number; echo?: number } = {}) => {
    node.connect(dry);
    const toReverb = track(context.createGain());
    toReverb.gain.value = sends.reverb ?? 1;
    node.connect(toReverb);
    toReverb.connect(reverbSend);
    if (sends.echo) {
      const toEcho = track(context.createGain());
      toEcho.gain.value = sends.echo;
      node.connect(toEcho);
      toEcho.connect(echoSend);
    }
  };

  /** A bus whose level follows the layer's presence, so the layer fades in and out. */
  const layerBus = (layer: MusicLayer, sends: { reverb?: number; echo?: number }) => {
    const bus = track(context.createGain());
    automate(bus.gain, (plan) => plan.presence[layer]);
    route(bus, sends);
    return bus;
  };

  // --- drones: the floor, always present, sliding with the key -------------
  const droneBus = track(context.createGain());
  route(droneBus, { reverb: 0.8 });
  const droneCount = 4 + rng.int(0, 2); // 4 or 5
  const dronePartials = [1, 1.5, 2, 3, 4];
  for (let index = 0; index < droneCount; index += 1) {
    const partial = dronePartials[index] as number;
    const level = (DRONE_LEVEL / (index + 1)) * rng.range(0.75, 1.15);

    const oscillator = context.createOscillator();
    oscillator.type = index % 2 === 0 ? "sine" : "triangle";
    automate(oscillator.frequency, (plan) => rootOf(plan) * partial, KEY_GLIDE_SEC);
    oscillator.detune.setValueAtTime(rng.range(-9, 9), startAt);
    lfo(oscillator.detune, rng.range(0.031, 0.087), rng.range(3, 13));

    const gain = track(context.createGain());
    gain.gain.setValueAtTime(level, startAt);
    // Upper partials breathe more deeply, so the drone's colour keeps shifting.
    lfo(gain.gain, rng.range(0.02, 0.1), level * (index < 2 ? 0.35 : 0.8));

    oscillator.connect(gain);
    gain.connect(droneBus);
    startSource(oscillator);

    events.push({
      at: startAt,
      kind: "drone",
      frequency: rootOf(sections[0]!.plan) * partial,
      durationSec: horizonSec,
      gain: level,
    });
  }

  // --- pad: a soft chord that follows the section --------------------------
  const padBus = layerBus("pad", { reverb: 1, echo: 0.1 });
  const padFilter = track(context.createBiquadFilter());
  padFilter.type = "lowpass";
  padFilter.Q.value = 0.6;
  padFilter.frequency.setValueAtTime(rng.range(900, 1300), startAt);
  lfo(padFilter.frequency, rng.range(0.015, 0.04), rng.range(250, 450));
  padFilter.connect(padBus);
  const padTone = (plan: SectionPlan, voice: number) => {
    const tones = plan.chord;
    const step = voice < tones.length ? (tones[voice] as number) : (tones[0] as number) + 12;
    return semitones(rootOf(plan) * 4, step);
  };
  for (let voice = 0; voice < 4; voice += 1) {
    const voiceGain = track(context.createGain());
    const level = PAD_LEVEL * rng.range(0.8, 1.1);
    voiceGain.gain.setValueAtTime(level, startAt);
    lfo(voiceGain.gain, rng.range(0.03, 0.08), level * 0.5);
    voiceGain.connect(padFilter);
    for (const [type, spread] of [["triangle", -7], ["sawtooth", 7]] as const) {
      const oscillator = context.createOscillator();
      oscillator.type = type;
      automate(oscillator.frequency, (plan) => padTone(plan, voice), 12);
      oscillator.detune.setValueAtTime(spread + rng.range(-3, 3), startAt);
      const partGain = track(context.createGain());
      partGain.gain.value = type === "sawtooth" ? 0.35 : 1;
      oscillator.connect(partGain);
      partGain.connect(voiceGain);
      startSource(oscillator);
    }
  }

  // --- shimmer: two high, slowly panning tones ------------------------------
  const shimmerBus = layerBus("shimmer", { reverb: 1.2, echo: 0.5 });
  for (let voice = 0; voice < 2; voice += 1) {
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    automate(
      oscillator.frequency,
      (plan) => semitones(rootOf(plan) * 16, (plan.chord[voice * 2] ?? plan.chord[0]) as number),
      12,
    );
    lfo(oscillator.detune, rng.range(0.15, 0.35), rng.range(4, 9));
    const gain = track(context.createGain());
    gain.gain.setValueAtTime(SHIMMER_LEVEL, startAt);
    lfo(gain.gain, rng.range(0.05, 0.12), SHIMMER_LEVEL * 0.7);
    const pan = track(context.createStereoPanner());
    pan.pan.setValueAtTime(0, startAt);
    lfo(pan.pan, rng.range(0.02, 0.05), 0.7 * (voice === 0 ? 1 : -1));
    oscillator.connect(gain);
    gain.connect(pan);
    pan.connect(shimmerBus);
    startSource(oscillator);
  }

  // --- noise: air, swelling ---------------------------------------------------
  const noiseBus = layerBus("noise", { reverb: 0.6 });
  const noiseSource = context.createBufferSource();
  noiseSource.buffer = makeNoiseBuffer(context, () => rng.next(), 4);
  noiseSource.loop = true;
  const noiseFilter = track(context.createBiquadFilter());
  noiseFilter.type = "lowpass";
  noiseFilter.frequency.setValueAtTime(380, startAt);
  noiseFilter.Q.value = 0.7;
  lfo(noiseFilter.frequency, rng.range(0.012, 0.045), rng.range(90, 190));
  const noiseGain = track(context.createGain());
  const noiseLevel = NOISE_LEVEL * rng.range(0.6, 1);
  noiseGain.gain.setValueAtTime(noiseLevel, startAt);
  noiseSource.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(noiseBus);
  startSource(noiseSource);
  events.push({ at: startAt, kind: "noise", frequency: 380, durationSec: horizonSec, gain: noiseLevel });

  // --- discrete voices: plucks and bells, placed on the wall clock -----------
  const pluckBus = layerBus("pluck", { reverb: 0.9, echo: 1 });
  const bellBus = layerBus("bells", { reverb: 1, echo: 0.25 });
  // Soft glass: a fundamental with a little of the third harmonic.
  const glass = context.createPeriodicWave(new Float32Array([0, 1, 0, 0.18]), new Float32Array(4));

  // Strikes share a few fixed pan positions per bus. A panner per strike would
  // mean hundreds of idle nodes across a 15-minute horizon, all still processed.
  const PAN_STEP = 0.35;
  const panBuses = new Map<AudioNode, StereoPannerNode[]>();
  const panFor = (bus: AudioNode, position: number) => {
    let buses = panBuses.get(bus);
    if (!buses) {
      buses = [-2, -1, 0, 1, 2].map((step) => {
        const pan = track(context.createStereoPanner());
        pan.pan.value = step * PAN_STEP;
        pan.connect(bus);
        return pan;
      });
      panBuses.set(bus, buses);
    }
    const index = Math.min(4, Math.max(0, Math.round(position / PAN_STEP) + 2));
    return buses[index] as StereoPannerNode;
  };

  const strike = (bus: AudioNode, at: number, frequency: number, attack: number, decay: number, peakGain: number, panAt: number) => {
    const oscillator = context.createOscillator();
    oscillator.setPeriodicWave(glass);
    oscillator.frequency.setValueAtTime(frequency, at);
    const gain = track(context.createGain());
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.linearRampToValueAtTime(peakGain, at + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
    oscillator.connect(gain);
    gain.connect(panFor(bus, panAt));
    startSource(oscillator, at);
    oscillator.stop(at + attack + decay + 0.05);
  };

  for (const section of sections) {
    const { plan } = section;
    const sectionEnd = section.at + SECTION_SEC;
    const root = rootOf(plan);

    if (plan.presence.pluck > 0) {
      const prng = createRng(`music:section:${plan.index}:plucks`);
      const interval = prng.range(1.2, 3.2);
      for (let at = section.at + prng.range(0, interval); at < sectionEnd; at += interval * prng.range(0.75, 1.3)) {
        const play = prng.chance(0.7);
        const step = prng.pick(plan.chord) + 12 * prng.int(0, 2);
        const decay = prng.range(1.8, 4);
        const peakGain = PLUCK_LEVEL * prng.range(0.6, 1);
        const panAt = prng.range(-0.7, 0.7);
        if (!play || at < startAt + 0.05 || at + decay > endsAt) continue;
        const frequency = semitones(root * 8, step);
        strike(pluckBus, at, frequency, 0.008, decay, peakGain, panAt);
        events.push({ at, kind: "pluck", frequency, durationSec: decay, gain: peakGain });
      }
    }

    if (plan.presence.bells > 0) {
      const brng = createRng(`music:section:${plan.index}:bells`);
      for (let at = section.at + brng.range(1.5, 6); at < sectionEnd; at += brng.range(5.5, 17)) {
        const step = brng.pick(plan.chord);
        const octave = brng.pick([1, 2]);
        const attack = brng.range(1.5, 5);
        const decay = brng.range(8, 20);
        const peakGain = BELL_LEVEL * brng.range(0.4, 1);
        const panAt = brng.range(-0.5, 0.5);
        if (at < startAt + 0.05 || at + attack + decay > endsAt) continue;
        const frequency = semitones(root * 4 * octave, step);
        strike(bellBus, at, frequency, attack, decay, peakGain, panAt);
        events.push({ at, kind: "bell", frequency, durationSec: attack + decay, gain: peakGain });
      }
    }

    for (const layer of ["pad", "shimmer"] as const) {
      if (plan.presence[layer] === 0) continue;
      events.push({
        at: Math.max(section.at, startAt),
        kind: layer,
        frequency: layer === "pad" ? padTone(plan, 0) : semitones(root * 16, plan.chord[0] as number),
        durationSec: SECTION_SEC,
        gain: plan.presence[layer],
      });
    }
  }
  events.sort((a, b) => a.at - b.at);

  let stopped = false;

  return {
    output,
    events,
    startAt,
    endsAt,
    stop(when, fadeSec = 2) {
      if (stopped) return;
      stopped = true;
      const from = Math.max(when, startAt);
      output.gain.cancelScheduledValues(from);
      output.gain.setValueAtTime(output.gain.value, from);
      output.gain.linearRampToValueAtTime(0, from + fadeSec);
      for (const source of sources) {
        try {
          source.stop(from + fadeSec + 0.05);
        } catch {
          // already stopped
        }
      }
    },
    dispose() {
      for (const source of sources) {
        try {
          source.stop();
        } catch {
          // already stopped
        }
        try {
          source.disconnect();
        } catch {
          // already disconnected
        }
      }
      for (const node of nodes) {
        try {
          node.disconnect();
        } catch {
          // already disconnected
        }
      }
    },
  };
}

/**
 * Keeps a bed covering the audible future. Rebuilds with a crossfade before the
 * current bed's horizon runs out, so the station never runs out of music.
 *
 * The bed is built with a fade-in even on rebuilds: the previous bed is still
 * sounding underneath during the crossfade, and a hard start on top of it would
 * click. Both beds follow the same wall-clock arrangement, so the crossfade
 * joins the same chord and the same layers.
 */
export class MusicDirector {
  private bed: MusicBed | null = null;
  private destination: AudioNode | null = null;
  /**
   * Superseded beds awaiting delayed disposal, with their timers. The bed is
   * retained alongside its timer so `dispose()` can tear it down immediately
   * instead of merely cancelling the timer and leaking the bed's nodes.
   */
  private pendingDisposals = new Map<number, MusicBed>();

  constructor(
    private readonly options: {
      horizonSec?: number;
      /** Rebuild this many seconds before the horizon ends. */
      rebuildLeadSec?: number;
      seedForSlot?: (slotIndex: number) => string | number;
    } = {},
  ) {}

  get current(): MusicBed | null {
    return this.bed;
  }

  attach(destination: AudioNode): void {
    this.destination = destination;
  }

  /**
   * `wallClockMs` is the wall clock at `contextNow`. It places the arrangement,
   * so it should always be passed; without it the slot start is used, which is
   * up to a minute early.
   */
  ensure(context: AudioContext, contextNow: number, slotIndex: number, wallClockMs?: number): MusicBed | null {
    const destination = this.destination;
    if (!destination) return this.bed;
    const horizon = this.options.horizonSec ?? 900;
    const lead = this.options.rebuildLeadSec ?? 120;
    if (this.bed && contextNow < this.bed.endsAt - lead) return this.bed;

    if (this.bed) {
      const previous = this.bed;
      // The old bed is faded out under the new one and disposed off the audio
      // thread. It is registered immediately so `dispose()` can cancel it.
      previous.stop(contextNow, 2.5);
      const timer = setTimeout(() => {
        this.pendingDisposals.delete(timer as unknown as number);
        previous.dispose();
      }, 4_000) as unknown as number;
      this.pendingDisposals.set(timer, previous);
    }

    const seed = this.options.seedForSlot?.(slotIndex) ?? slotIndex;
    const startLeadSec = 0.05;
    const bed = buildMusicBed(context, {
      seed,
      startTime: contextNow + startLeadSec,
      horizonSec: horizon,
      fadeInSec: 3,
      wallClockMs: (wallClockMs ?? slotIndex * 60_000) + startLeadSec * 1000,
    });
    bed.output.connect(destination);
    this.bed = bed;
    return bed;
  }

  dispose(): void {
    // Cancel every pending timer and dispose the beds it was holding. Clearing
    // the timers alone would strand the superseded beds' nodes.
    for (const [timer, bed] of this.pendingDisposals) {
      clearTimeout(timer);
      bed.dispose();
    }
    this.pendingDisposals.clear();
    this.bed?.dispose();
    this.bed = null;
  }
}
